import {
    LiveGuidelineCatalogSearch,
    LiveGuidelineCriterion,
    LiveGuidelineStoredCriteria,
} from '../redux/actions/liveGuidelineActions/types';
import { MasterFormatNode } from '../models/masterFormat/types';

export interface LiveGuidelineCriteriaEntry {
    label?: string;
    value: string;
}

export interface LiveGuidelineCriteriaGroup {
    title: string;
    entries: LiveGuidelineCriteriaEntry[];
}

const disclosureLabels: Record<string, string> = {
    productSpecific: 'Product-specific',
    optimized: 'Optimized',
    smProductSpecific: 'Product-specific',
    smIndustryAverage: 'Industry average',
    healthProductDeclaration: 'Health Product Declaration (HPD)',
    'declare-lbcRedListFree': 'LBC Red List Free',
    'declare-lbcCompliant': 'LBC Compliant',
    'declare-declared': 'Declared',
    'c2cCertified-platinum': 'Platinum',
    'c2cCertified-gold': 'Gold',
    'c2cCertified-silver': 'Silver',
    'c2cCertified-bronze': 'Bronze',
};

const lowEmittingLabels: Record<string, string> = {
    hasClearchem: 'ClearChem',
    hasCleanAirSilver: 'Clean Air Silver',
    hasCleanAirGold: 'Clean Air Gold',
    hasGreenCertified: 'Green Certified',
    hasFloorscore: 'FloorScore',
    hasIndoorAdvantage: 'Indoor Advantage',
    hasIndoorAdvantageGold: 'Indoor Advantage Gold',
    hasGreenguard: 'GREENGUARD',
    hasGreenguardGold: 'GREENGUARD Gold',
};

const ec3PercentileLabels: Record<string, string> = {
    '5': '80th+ percentile',
    '4': '60th+ percentile',
    '3': '60th percentile',
    '2': '40th percentile',
    '1': '20th percentile',
};

const splitValues = (value?: string): string[] =>
    value
        ? value
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
        : [];

const isStoredCriteria = (value: unknown): value is LiveGuidelineStoredCriteria => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const stored = value as Partial<LiveGuidelineStoredCriteria>;
    return (
        !!stored.catalogSearch &&
        typeof stored.catalogSearch === 'object' &&
        !!stored.catalogSearch.filters &&
        typeof stored.catalogSearch.filters === 'object'
    );
};

const addGroup = (
    groups: LiveGuidelineCriteriaGroup[],
    title: string,
    entries: LiveGuidelineCriteriaEntry[],
) => {
    groups.push({
        title,
        entries: entries.length ? entries : [{ value: 'None selected' }],
    });
};

const masterFormatCode = (title: string): string =>
    title.match(/^((?:\d{2}|XX)(?:\s+(?:\d{2}|XX)){0,2}(?:\.\d+)?)(?=\s|$)/)?.[1] ?? title;

const divisionName = (division?: string | null): string | undefined =>
    division?.replace(/^\d{2}\s+/, '').trim() || undefined;

// Non-breaking spaces keep a code like "03 05 XX" on one line; lines only wrap between codes.
const keepTogether = (code: string) => code.replace(/ /g, '\u00A0');

/**
 * Shortens a list of MasterFormat codes using heading codes only:
 * "NN 00 00" selected -> "NN XX XX"; "NN MM 00" (or "NN MM") selected -> "NN MM XX";
 * a level-4 code ("NN MM SS.dd") is hidden when its parent "NN MM SS" is selected; others stay as is.
 */
const collapseMasterFormatCodes = (codes: string[]): string[] => {
    const unique = [...new Set(codes)];
    const selected = new Set(unique);
    const parsed = unique.map((code) => code.split(/\s+/));
    const isCode = (parts: string[]) =>
        parts.length >= 2 &&
        parts.length <= 3 &&
        parts.every((part, index) => (index === 2 ? /^\d{2}(\.\d+)?$/ : /^\d{2}$/).test(part));
    const isHeading = (parts: string[]) => isCode(parts) && (parts[2] ?? '00') === '00';

    const fullDivisions = new Set(
        parsed.filter((parts) => isHeading(parts) && parts[1] === '00').map((parts) => parts[0]),
    );
    const fullFamilies = new Set(
        parsed.filter(isHeading).map((parts) => `${parts[0]} ${parts[1]}`),
    );

    const collapsed = new Set<string>();
    parsed.forEach((parts, index) => {
        if (!isCode(parts)) {
            collapsed.add(unique[index]);
        } else if (fullDivisions.has(parts[0])) {
            collapsed.add(keepTogether(`${parts[0]} XX XX`));
        } else if (fullFamilies.has(`${parts[0]} ${parts[1]}`)) {
            collapsed.add(keepTogether(`${parts[0]} ${parts[1]} XX`));
        } else if (
            parts[2]?.includes('.') &&
            selected.has(`${parts[0]} ${parts[1]} ${parts[2].split('.')[0]}`)
        ) {
            // Covered by its selected parent section.
        } else {
            collapsed.add(keepTogether(parts.join(' ')));
        }
    });
    return [...collapsed].sort((a, b) => a.localeCompare(b));
};

// How a fully selected node is shown: a division as "NN XX XX", a section with sub-sections as "NN MM XX", otherwise its own code.
const masterFormatNodeCode = (node: MasterFormatNode): string => {
    if (!node.number) {
        return node.title;
    }
    const [division, section] = node.number.split(/\s+/);
    if (node.children.length && node.level === 1) {
        return keepTogether(`${division} XX XX`);
    }
    if (node.children.length && node.level === 2) {
        return keepTogether(`${division} ${section} XX`);
    }
    return keepTogether(node.number);
};

// Uses Transparency Catalog's MasterFormat tree to know when a whole division/section is selected.
const collapseWithMasterFormatTree = (
    tree: MasterFormatNode[],
    selectedIds: Set<string>,
): { entries: LiveGuidelineCriteriaEntry[]; unknownIds: string[] } => {
    const isCovered = (node: MasterFormatNode): boolean =>
        selectedIds.has(String(node.id)) ||
        (node.children.length > 0 && node.children.every(isCovered));

    const knownIds = new Set<string>();
    const indexTree = (node: MasterFormatNode) => {
        knownIds.add(String(node.id));
        node.children.forEach(indexTree);
    };
    tree.forEach(indexTree);

    const entries: LiveGuidelineCriteriaEntry[] = [];
    tree.forEach((division) => {
        const codes: string[] = [];
        const collect = (node: MasterFormatNode) => {
            if (isCovered(node)) {
                codes.push(masterFormatNodeCode(node));
            } else {
                node.children.forEach(collect);
            }
        };
        collect(division);
        if (codes.length) {
            entries.push({ label: division.title, value: codes.join(', ') });
        }
    });

    return { entries, unknownIds: [...selectedIds].filter((id) => !knownIds.has(id)) };
};

const addMasterFormatGroup = (
    groups: LiveGuidelineCriteriaGroup[],
    search: LiveGuidelineCatalogSearch,
    consumed: Set<string>,
    masterFormatTree: MasterFormatNode[] | null,
) => {
    const numbers = splitValues(search.filters.number);
    consumed.add('division');
    consumed.add('number');
    const sectionLabels = search.labels?.number ?? [];
    const sectionTitles = sectionLabels.map((item) => item.title).filter(Boolean);

    // Without the tree (not loaded / failed) shorten from the codes alone.
    const codeOnlyEntries = (): LiveGuidelineCriteriaEntry[] => [
        {
            label: divisionName(search.labels?.division),
            value: sectionTitles.length
                ? collapseMasterFormatCodes(sectionTitles.map(masterFormatCode)).join(', ')
                : numbers.length
                ? numbers.join(', ')
                : 'None selected',
        },
    ];

    if (!masterFormatTree) {
        addGroup(groups, 'MasterFormat® sections', codeOnlyEntries());
        return;
    }

    const selectedIds = new Set([...numbers, ...sectionLabels.map((item) => item.id)]);
    const { entries, unknownIds } = collapseWithMasterFormatTree(masterFormatTree, selectedIds);

    // Ids the tree doesn't know (e.g. a category that no longer has products) keep the code-based rule.
    const unknownTitles = sectionLabels
        .filter((item) => unknownIds.includes(item.id))
        .map((item) => masterFormatCode(item.title));
    if (unknownTitles.length) {
        const label = divisionName(search.labels?.division);
        const unknownCodes = collapseMasterFormatCodes(unknownTitles).join(', ');
        const sameDivision = entries.find((entry) => entry.label === label);
        if (sameDivision) {
            sameDivision.value = `${sameDivision.value}, ${unknownCodes}`;
        } else {
            entries.push({ label, value: unknownCodes });
        }
    }

    addGroup(groups, 'MasterFormat® sections', entries);
};

const formatEpdEntries = (disclosures: string[]): LiveGuidelineCriteriaEntry[] => {
    const allEpd = disclosures.filter((value) => ['productSpecific', 'optimized'].includes(value));
    const smEpd = disclosures.filter((value) =>
        ['smProductSpecific', 'smIndustryAverage'].includes(value),
    );
    const entries: LiveGuidelineCriteriaEntry[] = [];

    if (allEpd.length) {
        entries.push({
            label: 'All EPDs',
            value: allEpd.map((value) => disclosureLabels[value] || value).join(', '),
        });
    }
    if (smEpd.length) {
        entries.push({
            label: 'Sustainable Minds Transparency Reports (EPD)*',
            value: smEpd.map((value) => disclosureLabels[value] || value).join(', '),
        });
    }
    return entries;
};

const formatMaterialEntries = (disclosures: string[]): LiveGuidelineCriteriaEntry[] => {
    const entries: LiveGuidelineCriteriaEntry[] = [];
    if (disclosures.includes('healthProductDeclaration')) {
        entries.push({ value: disclosureLabels.healthProductDeclaration });
    }

    const declare = disclosures
        .filter((value) => value.startsWith('declare-'))
        .map((value) => disclosureLabels[value] || value);
    if (declare.length) {
        entries.push({ label: 'Declare', value: declare.join(', ') });
    } else if (disclosures.includes('declare')) {
        entries.push({ value: 'Declare' });
    }

    const c2cLevels = disclosures
        .filter((value) => value.startsWith('c2cCertified-'))
        .map((value) => disclosureLabels[value] || value);
    if (c2cLevels.length) {
        entries.push({ label: 'C2C Certified*', value: c2cLevels.join(', ') });
    } else if (disclosures.includes('c2cCertified')) {
        entries.push({ label: 'C2C Certified*', value: 'Certified' });
    }

    return entries;
};

const addDisclosureGroups = (
    groups: LiveGuidelineCriteriaGroup[],
    filters: Record<string, string>,
    consumed: Set<string>,
) => {
    const disclosures = splitValues(filters.disclosure);
    const epdTypes = ['productSpecific', 'optimized', 'smProductSpecific', 'smIndustryAverage'];
    addGroup(
        groups,
        'EPD types / embodied carbon',
        formatEpdEntries(disclosures.filter((value) => epdTypes.includes(value))),
    );
    addGroup(
        groups,
        'Material disclosure types',
        formatMaterialEntries(disclosures.filter((value) => !epdTypes.includes(value))),
    );
    if (filters.disclosure !== undefined) {
        consumed.add('disclosure');
    }
};

const addEc3Group = (
    groups: LiveGuidelineCriteriaGroup[],
    filters: Record<string, string>,
    consumed: Set<string>,
) => {
    const values = splitValues(filters.ec3);
    if (values.length) {
        const epdGroup = groups.find((group) => group.title === 'EPD types / embodied carbon');
        const entry = {
            label: 'EC3 Embodied Carbon',
            value: values.map((value) => ec3PercentileLabels[value] || value).join(', '),
        };
        if (epdGroup) {
            if (epdGroup.entries[0]?.value === 'None selected') {
                epdGroup.entries = [entry];
            } else {
                epdGroup.entries.push(entry);
            }
        } else {
            addGroup(groups, 'EPD types / embodied carbon', [entry]);
        }
    }
    if (filters.ec3 !== undefined) {
        consumed.add('ec3');
    }
};

const addLowEmittingGroups = (
    groups: LiveGuidelineCriteriaGroup[],
    filters: Record<string, string>,
    consumed: Set<string>,
) => {
    const values = splitValues(filters.iaq);
    if (filters.iaq !== undefined) {
        consumed.add('iaq');
    }
    addGroup(
        groups,
        'Low-emitting types',
        values.map((value) => ({ value: lowEmittingLabels[value] || value })),
    );
    if (filters.iaqRegulation !== undefined) {
        consumed.add('iaqRegulation');
        addGroup(
            groups,
            'Low-emitting regulations',
            splitValues(filters.iaqRegulation).map((value) => ({
                value: value === 'hasCdphCompliant' ? 'CDPH compliant' : value,
            })),
        );
    }
};

const addBrandGroup = (
    groups: LiveGuidelineCriteriaGroup[],
    search: LiveGuidelineCatalogSearch,
    consumed: Set<string>,
    brandNamesById: Map<string, string> | null,
) => {
    const { filters } = search;
    if (filters.brandExclude === undefined) {
        addGroup(groups, 'Excluded brands', []);
        return;
    }
    consumed.add('brandExclude');
    const brands = splitValues(filters.brandExclude);
    const brandLabels = search.labels?.brand ?? [];
    const namedBrands = brands.map(
        (brandId) =>
            brandNamesById?.get(brandId) ??
            brandLabels.find((brand) => brand.id === brandId)?.title ??
            brandId,
    );
    addGroup(groups, 'Excluded brands', brands.length ? [{ value: namedBrands.join(', ') }] : []);
};

const formatStoredCriteria = (
    catalogSearch: LiveGuidelineCatalogSearch,
    masterFormatTree: MasterFormatNode[] | null,
    brandNamesById: Map<string, string> | null,
): LiveGuidelineCriteriaGroup[] => {
    const groups: LiveGuidelineCriteriaGroup[] = [];
    const consumed = new Set<string>(['catalog']);
    addMasterFormatGroup(groups, catalogSearch, consumed, masterFormatTree);
    addDisclosureGroups(groups, catalogSearch.filters, consumed);
    addEc3Group(groups, catalogSearch.filters, consumed);
    addLowEmittingGroups(groups, catalogSearch.filters, consumed);
    addBrandGroup(groups, catalogSearch, consumed, brandNamesById);
    return groups;
};

// Ids referenced by brandExclude filters across all criteria, for fetching their names up front.
export const collectExcludedBrandIds = (criteria: LiveGuidelineCriterion[]): string[] => {
    const ids = new Set<string>();
    criteria.forEach((criterion) => {
        if (!criterion.filterValue) {
            return;
        }
        try {
            const parsed = JSON.parse(criterion.filterValue);
            if (isStoredCriteria(parsed)) {
                splitValues(parsed.catalogSearch.filters.brandExclude).forEach((id) => ids.add(id));
            }
        } catch {
            // Not stored criteria JSON; nothing to collect.
        }
    });
    return [...ids];
};

// Builds an id -> company name lookup from fetched BrandCompany records.
export const buildBrandNamesById = (
    brands: Array<{ id: number | string; company: string }>,
): Map<string, string> => new Map(brands.map((brand) => [String(brand.id), brand.company]));

export const formatLiveGuidelineCriteria = (
    criteria: LiveGuidelineCriterion[],
    masterFormatTree: MasterFormatNode[] | null = null,
    brandNamesById: Map<string, string> | null = null,
): LiveGuidelineCriteriaGroup[] => {
    const groups = criteria.flatMap((criterion) => {
        if (!criterion.filterValue) {
            return [];
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(criterion.filterValue);
        } catch {
            return [{ title: 'Search criteria', entries: [{ value: criterion.filterValue }] }];
        }

        if (!isStoredCriteria(parsed)) {
            return [{ title: 'Search criteria', entries: [{ value: criterion.filterValue }] }];
        }

        return formatStoredCriteria(parsed.catalogSearch, masterFormatTree, brandNamesById);
    });

    return groups.reduce<LiveGuidelineCriteriaGroup[]>((combined, group) => {
        const existing = combined.find((item) => item.title === group.title);
        if (existing) {
            const existingHasValues = existing.entries.some(
                (entry) => entry.value !== 'None selected',
            );
            const nextHasValues = group.entries.some((entry) => entry.value !== 'None selected');
            if (nextHasValues) {
                if (!existingHasValues) {
                    existing.entries = [];
                }
                existing.entries.push(...group.entries);
            }
        } else {
            combined.push(group);
        }
        return combined;
    }, []);
};
